import { InfoIcon, Progress, Tooltip } from "@au-schein-de/au-ui-library";
import { Trans, useTranslation } from "react-i18next";
import { useCart } from "./cart.context";

function getMonthRenewalDate(): string {
	const now = new Date();
	const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
	const day = String(nextMonth.getDate()).padStart(2, "0");
	const month = String(nextMonth.getMonth() + 1).padStart(2, "0");
	const year = String(nextMonth.getFullYear()).slice(-2);
	return `${day}.${month}.${year}`;
}

function OverLimitProgressBar({
	maxQuantity,
	overAmount,
}: {
	maxQuantity: number;
	overAmount: number;
}) {
	const total = maxQuantity + overAmount;
	const greenWidth = (maxQuantity / total) * 100;
	const redWidth = (overAmount / total) * 100;

	return (
		<div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200">
			<div
				className="absolute left-0 top-0 h-full bg-ansay-400 rounded-l-full"
				style={{ width: `${greenWidth}%` }}
			/>
			<div
				className="absolute top-0 h-full bg-error-500 rounded-r-full"
				style={{ left: `${greenWidth}%`, width: `${redWidth}%` }}
			/>
		</div>
	);
}

export function CartProgressBar() {
	const { t } = useTranslation("cart");
	const { remainingCapacity, maxTotalCartQuantity } = useCart();

	const isOverLimit = remainingCapacity < 0;
	const renewalDate = getMonthRenewalDate();

	const usedAmount = maxTotalCartQuantity - remainingCapacity;
	const progressValue = Math.min(
		100,
		Math.max(0, (usedAmount / maxTotalCartQuantity) * 100),
	);

	return (
		<div className="bg-gray-100 rounded-xl p-3 flex flex-col gap-2 w-full">
			<div className="flex items-center justify-between">
				<span
					className="flex items-center gap-1 text-body-sm text-gray-700"
					data-testid="cart-limit-summary"
				>
					{isOverLimit ? (
						<Trans
							i18nKey="limit.overLimit"
							ns="cart"
							values={{
								amount: remainingCapacity,
								renewalDate,
							}}
							components={{
								red: <span className="text-body-sm-emphasis text-error-600" />,
							}}
						/>
					) : (
						<Trans
							i18nKey="limit.remaining"
							ns="cart"
							values={{ remainingCapacity, renewalDate }}
							components={{
								bold: <span className="text-body-sm-emphasis" />,
							}}
						/>
					)}
				</span>
				<Tooltip.Provider>
					<Tooltip.Root>
						<Tooltip.Trigger asChild>
							<button
								type="button"
								aria-label={t("limit.tooltip", { maxTotalCartQuantity })}
								className="inline-flex cursor-pointer text-ansay-700"
							>
								<InfoIcon size={6} />
							</button>
						</Tooltip.Trigger>
						<Tooltip.Content
							side="bottom"
							align="end"
							className="max-w-100 bg-white shadow-md"
						>
							{t("limit.tooltip", { maxTotalCartQuantity })}
						</Tooltip.Content>
					</Tooltip.Root>
				</Tooltip.Provider>
			</div>
			{isOverLimit ? (
				<OverLimitProgressBar
					maxQuantity={maxTotalCartQuantity}
					overAmount={Math.abs(remainingCapacity)}
				/>
			) : (
				<Progress value={progressValue} className="[&>div]:bg-ansay-400" />
			)}
		</div>
	);
}
